import React, { useMemo, useState } from 'react';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { assessmentsService } from '../api/assessmentManagementService';
const AssessmentCreate: React.FC = () => {
    const queryClient = useQueryClient();
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin', 'assessments'],
        queryFn: () => assessmentsService.list(),
    });
    const [name, setName] = useState('');
    const [date, setDate] = useState(''); // YYYY-MM-DD
    const [location, setLocation] = useState('');
    const [isDoubleAssessment, setIsDoubleAssessment] = useState(false);
    const canCreate = useMemo(() => name.trim().length > 0 && date.trim().length > 0, [name, date]);
    const createMutation = useMutation({
        mutationFn: async () => assessmentsService.create({ name: name.trim(), date: date.trim(), location: location.trim() || null, isDoubleAssessment }),
        onSuccess: async () => {
            setName('');
            setDate('');
            setLocation('');
            setIsDoubleAssessment(false);
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments'] });
        },
    });
    return (<Layout>
      <div className="space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <h1 className="text-2xl font-bold text-gray-900">Assessments</h1>
          <p className="text-gray-600 mt-2">
            Create assessment events, manage participants, and build schedules.
          </p>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Assessment Event</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input className="border border-gray-300 rounded-md px-3 py-2" placeholder='Name (e.g. "February 2025 Assessment")' value={name} onChange={(e) => setName(e.target.value)}/>
            <label className="border border-gray-300 rounded-md px-3 py-2 flex items-center gap-3 bg-gray-50">
              <input type="checkbox" checked={isDoubleAssessment} onChange={(e) => setIsDoubleAssessment(e.target.checked)} className="h-4 w-4 text-primary-600"/>
              <span className="text-sm font-medium text-gray-800">Double assessment</span>
            </label>
            <input className="border border-gray-300 rounded-md px-3 py-2" type="date" value={date} onChange={(e) => setDate(e.target.value)}/>
            <input className="border border-gray-300 rounded-md px-3 py-2" placeholder="Location (optional)" value={location} onChange={(e) => setLocation(e.target.value)}/>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50" disabled={!canCreate || createMutation.isPending} onClick={() => createMutation.mutate()}>
              Create
            </button>
            {createMutation.isError && (<div className="text-sm text-red-700">{(createMutation.error as any)?.message || 'Failed to create'}</div>)}
          </div>
        </div>

        {isLoading && (<div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">
            Loading…
          </div>)}

        {error && (<div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-red-700">
            Failed to load assessments.
          </div>)}

        {!isLoading && !error && (<div className="bg-white shadow-lg rounded-xl overflow-hidden">
            <div className="px-4 py-4 sm:px-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">All Assessment Events</h2>
              <Link to="/assessments/assessors" className="inline-flex items-center px-3 py-2 rounded-md bg-gray-100 text-gray-900 hover:bg-gray-200 text-sm">
                Manage assessors
              </Link>
            </div>
            <ResponsiveTableWrapper>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(data?.data || []).map((a) => (<tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {a.name}
                        {a.isDoubleAssessment && (<span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Double</span>)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {new Date(a.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{a.location || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/assessments/${a.id}/manage`} className="inline-flex items-center px-3 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 text-sm">
                          Manage
                        </Link>
                      </td>
                    </tr>))}
                  {(data?.data || []).length === 0 && (<tr>
                      <td className="px-4 py-6 text-sm text-gray-600" colSpan={4}>
                        No assessments found.
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </ResponsiveTableWrapper>
          </div>)}
      </div>
    </Layout>);
};
export default AssessmentCreate;
